import { useQueryClient } from '@tanstack/react-query';
import {
  useGetDeliverObjectDependencyProjectProjectId,
  useGetDeliverObjectDependencyDeliverObjectDeliverObjectId,
  usePostDeliverObjectDependency,
  usePutDeliverObjectDependencyId,
  useDeleteDeliverObjectDependencyId,
} from '../api/deliver-object-dependency-api/deliver-object-dependency-api';
import type { DeliverObjectDependencyDto } from '../api/model';

const useInvalidateDependencies = () => {
  const queryClient = useQueryClient();
  return async () => {
    return await queryClient.refetchQueries({
      predicate: query => {
        const first = query.queryKey?.[0];
        if (typeof first === 'string') {
          return first.startsWith('/deliver-object-dependency');
        }
        const second = query.queryKey?.[1];
        return typeof second === 'string' && second.startsWith('/deliver-object-dependency');
      },
      type: 'active',
    });
  };
};

export const useProjectDependencies = (projectId?: number) => {
  const query = useGetDeliverObjectDependencyProjectProjectId(projectId as number, {
    query: {
      enabled: !!projectId,
      staleTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    },
  });

  return {
    dependencies: (query.data ?? []) as DeliverObjectDependencyDto[],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};

export const useDeliverableDependencies = (deliverObjectId?: number) => {
  const query = useGetDeliverObjectDependencyDeliverObjectDeliverObjectId(
    deliverObjectId as number,
    {
      query: {
        enabled: !!deliverObjectId,
        staleTime: 0,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
      },
    }
  );

  return {
    dependencies: (query.data ?? []) as DeliverObjectDependencyDto[],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};

export const useCreateDependency = () => {
  const invalidate = useInvalidateDependencies();
  return usePostDeliverObjectDependency({
    mutation: {
      onSuccess: async () => {
        await invalidate();
      },
    },
  });
};

// Update a dependency and refresh all dependency views.
export const useUpdateDependency = () => {
  const invalidate = useInvalidateDependencies();
  return usePutDeliverObjectDependencyId({
    mutation: {
      onSuccess: async () => {
        await invalidate();
      },
    },
  });
};

export const useDeleteDependency = () => {
  const invalidate = useInvalidateDependencies();
  return useDeleteDeliverObjectDependencyId({
    mutation: {
      onSuccess: async () => {
        await invalidate();
      },
    },
  });
};
